import { l } from '@adornis/base/rxjs-extension/rxjs-helpers.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { InstitutionOffer } from '@adornis/bh-registration/api/institution-offer.js';
import {
  activeProductsSelectionSet,
  activeWorkshopSetsSelectionSet,
  RegistrationController,
} from '@adornis/bh-registration/client/bh-registration-controller.js';
import {
  counselingCardBuildifyPreset,
  workshopCardBuildifyPreset,
  workshopSetCardBuildifyPreset,
} from '@adornis/buchungsheld-db/_buildify-presets.js';
import { subscribeMaterialiedProductsById } from '@adornis/buchungsheld-db/api/product.js';
import { areTimeSlotOverlapping } from '@adornis/buchungsheld-db/datetime/generate-start-times-for-multiple-overlapping-timeslots.js';
import { Einzelberatung, EinzelberatungSlot } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import type { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import type { RegistrationEinzelberatungData } from '@adornis/buchungsheld-db/entities/RegistrationDataBuffer.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { WorkshopSet } from '@adornis/buchungsheld-db/entities/WorkshopSet.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { productPriceNumber } from '@adornis/buchungsheld-db/global-helpers.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { obs } from '@adornis/chemistry/directives/observable.js';
import { provideContext } from '@adornis/functional-lit/directive-provide-context.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, map, merge, of, startWith, switchMap } from 'rxjs';
import { getRegistrationColorDefinitionsForInstitution } from './productCardShared.js';
import { counselingCardDataContext, type CounselingCardData } from './renderCounselingCard.js';
import { workshopCardDataContext, type WorkshopCardData } from './renderWorkshopCard.js';
import { workshopSetCardDataContext, type WorkshopSetCardData } from './renderWorkshopSetCard.js';

type InstitutionOfferBase = {
  institution: NarrowedEntity<
    Institution,
    { _id: 1; productPageWorkshopCard: 1; productPageCounselingCard: 1; productPageWorkshopSetCard: 1 }
  >;
};
type InstitutionOfferData =
  | (InstitutionOfferBase & { type: 'workshop'; workshopData: WorkshopCardData })
  | (InstitutionOfferBase & { type: 'counseling'; counselingData: CounselingCardData })
  | (InstitutionOfferBase & { type: 'workshopset'; workshopSetData: WorkshopSetCardData });

export function workshopIsTeaser(
  workshop: NarrowedEntity<Workshop, { _id: 1; timeframe: { isRegistrationPossibleBesidesTimeframe: 1; from: 1 } }>,
) {
  if (!workshop.timeframe) return [false, undefined] as const;
  if (workshop.timeframe.isRegistrationPossibleBesidesTimeframe) return [false, undefined] as const;
  if (workshop.timeframe.from <= DateTime.now()) return [false, undefined] as const;

  return [true, workshop.timeframe.from] as const;
}

export function getWorkshopCardDataFromWorkshop(
  registrationController: RegistrationController,
  workshop: Workshop,
  allActiveProductsForUser: Product[],
): WorkshopCardData {
  const product = workshop;

  const institution = registrationController.institution;
  const registrationColorDefinitions = getRegistrationColorDefinitionsForInstitution(institution);
  const [isTeaser, teaserDate] = workshopIsTeaser(product);
  const bookedAlready = allActiveProductsForUser.some(p => p?._id === product._id);
  const selected = registrationController.registrationData?.productData.find(
    productData => productData.productID === product._id,
  );

  const isBlockedByOtherSelection =
    !selected &&
    product.appointments.some(appointment =>
      registrationController.isTimeSlotoverlappingWithOtherTimeslots(appointment),
    );
  const isClickable = !isBlockedByOtherSelection && !bookedAlready && !isTeaser;
  const bundleMap = registrationController.bundleMappedByProductID;
  const applyedBundle = (() => {
    if (!bundleMap) return undefined;
    const bundleEntry = bundleMap[product._id];
    if (!bundleEntry) return undefined;
    if (!bundleEntry.hasPrice) return { price: undefined };
    return { price: bundleEntry.price.unitAmount };
  })();

  return {
    workshop: product,
    start: product.start,
    end: product.end,
    colorDef: registrationColorDefinitions,
    selected: !!selected,
    isTeaser,
    teaserDate,
    isClickable,
    bookedAlready,
    isBlockedByOtherSelection,
    price: productPriceNumber(product),
    applyedBundle,
  };
}

function getCounselingCardDataFromCounseling(
  registrationController: RegistrationController,
  counseling: Einzelberatung,
  allActiveProductsForUser: Product[],
): CounselingCardData {
  const product = counseling;

  const institution = registrationController.institution;
  const registrationColorDefinitions = getRegistrationColorDefinitionsForInstitution(institution);

  const bookedAlready = allActiveProductsForUser.some(p => p?._id === product._id);

  const slotData = product.slots
    .map(timeslot => {
      const productData = registrationController.registrationData?.productData.find(
        productData => productData.productID === product._id,
      );
      const selectedTimeslot = productData ? (productData as RegistrationEinzelberatungData).timeslotID : null;

      const isSelected = selectedTimeslot === timeslot._id;

      const hasTimeCollision =
        !isSelected && areTimeSlotOverlapping([timeslot, ...registrationController.allBlockingTimeslots]);

      return { timeslot, isSelected, hasTimeCollision };
    })
    .toSorted((a, b) => {
      const va = a.timeslot.start!.set({ second: 0, millisecond: 0 }).toMillis();
      const vb = b.timeslot.start!.set({ second: 0, millisecond: 0 }).toMillis();
      if (va !== vb) return va - vb; // sort by  starttime
      return a.timeslot.isBooked ? 1 : -1; // sort by booked when same
    })
    // remove same start date (so that only one slot per timeslot remains) (because of prevoius sorting, the remaining slot is always free, if there is a free slot at all)
    .filter((v, i, arr) => {
      const prevValue = arr[i - 1];
      if (!prevValue) return true; // keep first
      const vPrev = prevValue.timeslot.start!.set({ second: 0, millisecond: 0 }).toMillis();
      const vCurr = v.timeslot.start!.set({ second: 0, millisecond: 0 }).toMillis();
      if (vPrev === vCurr) return false; // filter out same startTime
      return true;
    }, []);

  return {
    counseling: product,
    start: product.slotConfig?.start,
    end: product.slotConfig?.end,
    slotData,
    bookedAlready,
    price: productPriceNumber(product),
    applyedBundle: undefined,
    colorDef: registrationColorDefinitions,
    events: {
      onSlotSelect: (timeslot: EinzelberatungSlot) => {
        registrationController.toggleSelectProdukt(product, { timeslot });
      },
    },
  };
}

function getWorkshopSetCardDataFromWorkshopset(
  registrationController: RegistrationController,
  workshopSet: WorkshopSet,
  allActiveProductsForUser: Product[],
): WorkshopSetCardData {
  const institution = registrationController.institution;
  const registrationColorDefinitions = getRegistrationColorDefinitionsForInstitution(institution);
  const workshopData = workshopSet.workshops.map(workshop =>
    getWorkshopCardDataFromWorkshop(registrationController, workshop, allActiveProductsForUser),
  );

  return {
    colorDef: registrationColorDefinitions,
    start: undefined,
    end: undefined,
    price: undefined,
    applyedBundle: undefined,
    workshopSet,
    workshops: workshopData,
    selected: workshopData.every(workshop => workshop.selected),
    isClickable: workshopData.every(workshop => workshop.isClickable),
    events: {
      onShowDetailPage: workshop => registrationController.showProductDetails(workshop._id),
      onWorkshopSelect: workshop => registrationController.toggleSelectProdukt([workshop]),
    },
  };
}

// TODO: get rid of this function
export function renderGroupedProductsByCategory({
  offer,
  registrationController,
}: {
  offer: InstitutionOffer;
  registrationController: RegistrationController;
}) {
  return html`
    <node-institution-products-helper
      .offer=${offer}
      .registrationController=${registrationController}
    ></node-institution-products-helper>
  `;
}

@customElement('node-institution-products-helper')
class NodeInstitutionProductsHelper extends ChemistryLitElement {
  @property({ attribute: false }) offer?: InstitutionOffer;
  @property({ attribute: false }) registrationController!: RegistrationController;

  @state() private readonly _language = new RXController(this, globalLanguageSubject, obs => obs.pipe(l('_language')));
  @state() private readonly _size = new WindowSizeController(this);
  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);
  @state() readonly openCategories: Set<string> = new Set();

  override render() {
    if (!this.offer || !this.registrationController) return html` infos are missing `;

    const getInstitutionOffers = (products: Product[], workshopSets: WorkshopSet[]) => {
      const availableWorkshopSets = workshopSets.map(workshopSet => {
        return [
          null,
          {
            type: 'workshopset',
            institution: this.registrationController.institution,
            workshopSetData: getWorkshopSetCardDataFromWorkshopset(
              this.registrationController,
              workshopSet,
              this.allActiveProductsForUser ?? [],
            ),
          },
        ];
      });

      const availableProducts = products.map(product => {
        if (product instanceof Einzelberatung)
          // TODO: return just offer
          return [
            product,
            {
              type: 'counseling',
              institution: this.registrationController.institution,
              counselingData: getCounselingCardDataFromCounseling(
                this.registrationController,
                product,
                this.allActiveProductsForUser ?? [],
              ),
            },
          ];

        if (product instanceof Workshop)
          return [
            product,
            {
              type: 'workshop',
              institution: this.registrationController.institution,
              workshopData: getWorkshopCardDataFromWorkshop(
                this.registrationController,
                product,
                this.allActiveProductsForUser ?? [],
              ),
            },
          ];
      });

      return [...availableWorkshopSets, ...(availableProducts ?? [])];
    };

    const offer = this.offer;
    if (!offer) {
      console.log('waiting for offer');
      return nothing;
    }

    const workshops = category => html`
      <x-flex
        space="md"
        ${css({
          padding: '10px 0',
          paddingLeft: '8px',

          ...(this._size.mobile && offer.categories.length >= 2
            ? {
                borderLeft: `2px solid ${this.colors.accent}`,
                marginLeft: `-${this.spacing.lg + -1}px`,
              }
            : {}),
        })}
      >
        ${obs(
          // TODO: somehow cache loaded products (cache hhas to be invalidated when filter changes)
          combineLatest([
            // sub products
            subscribeMaterialiedProductsById([
              ...(this.offer?.products.filter(p => p.categoryID === category._id).map(p => p._id) ?? []),
              ...(this.offer?.workshopSets ?? [])
                .filter(ws => ws.categoryID === category._id)
                .map(ws => ws.workshopIDs)
                .flat(),
            ])(activeProductsSelectionSet).pipe(
              map(products =>
                products.sort(
                  (p1, p2) =>
                    ((p1 as Workshop | Einzelberatung).startDate?.toMillis() ?? 0) -
                    ((p2 as Workshop | Einzelberatung).startDate?.toMillis() ?? 0),
                ),
              ),
            ),

            // sub workshop sets
            subscribeByID(
              WorkshopSet,
              this.offer?.workshopSets.filter(ws => ws.categoryID === category._id).map(ws => ws._id) ?? [],
            )(activeWorkshopSetsSelectionSet),
            // TODO: without this card selection does not work. Find a better way to do that (maybe provide card data?)
            merge(of(1), this.registrationController.selectedProducts$),
          ]).pipe(
            filter(([products, workshopSets]) => isDefined(products) && isDefined(workshopSets)),
            // TODO: without this card selection does not work. Find a better way to do that (maybe provide card data?)
            switchMap(([products, workshopSets]) =>
              merge(
                of([products, workshopSets] as const),
                this.registrationController.selectedProducts$.pipe(
                  map(selectedProducts => [products, workshopSets] as const),
                ),
              ),
            ),
            map(([products, workshopSets]) =>
              getInstitutionOffers(products, workshopSets).map(([product, offer]) => this._stateActive(product, offer)),
            ),
            startWith(this._translation.translate('loading')),
          ),
        )}
      </x-flex>
    `;

    return html`
      <x-flex space="xs">
        ${offer.categories.map(category => {
          if (offer.categories.length === 1) return workshops(category);

          const open = this.openCategories.has(category._id);
          return html`
            <tr-toggle-content
              .label=${html`
                <x-flex horizontal space-between crossaxis-center space="md" w100>
                  <tr-text>${category.name}</tr-text>
                </x-flex>
              `}
              ?open=${open}
              @open-changed=${(e: Event & { detail: { value: boolean } }) => {
                const open = e.detail.value;
                if (open) this.openCategories.add(category._id);
                else this.openCategories.delete(category._id);
                this.requestUpdate();
              }}
            >
              ${open ? workshops(category) : nothing}
            </tr-toggle-content>
          `;
        })}
      </x-flex>
    `;
  }

  // TODO: just pass the offer and handle selection through dom events
  private _stateActive(product: Product, offer: InstitutionOfferData) {
    const institution = offer.institution;

    if (offer.type === 'workshopset') {
      const content = (() => {
        if (typeof institution.productPageWorkshopSetCard === 'string')
          return unsafeHTML(institution.productPageWorkshopSetCard);

        return unsafeHTML(workshopSetCardBuildifyPreset);
      })();

      const workshopSetCardData = offer.workshopSetData;

      const onClick = () => {
        if (workshopSetCardData.isClickable) {
          const wss = workshopSetCardData.workshopSet.workshops;
          if (workshopSetCardData.selected) this.registrationController.toggleSelectProdukt(wss, { forceFalse: true });
          else this.registrationController.toggleSelectProdukt(wss, { forceTrue: true });
          this.requestUpdate();
        }
      };

      return html`<bh-workshop-set-card
        @click=${onClick}
        ${provideContext(workshopSetCardDataContext, workshopSetCardData)}
        ${css({ width: 'calc(100% - 20px)' })}
        .workshopSetCardData=${workshopSetCardData}
      >
        <div ${css({ width: '100%' })} ${acss({ ' > *': { width: '100%' } })}>${content}</div>
      </bh-workshop-set-card> `;
    }

    if (offer.type === 'workshop') {
      const content = (() => {
        if (typeof institution.productPageWorkshopCard === 'string') {
          return unsafeHTML(institution.productPageWorkshopCard);
        }

        return unsafeHTML(workshopCardBuildifyPreset);
      })();

      const workshopCardData = offer.workshopData;

      const onClick = () =>
        workshopCardData.isClickable ? this.registrationController.toggleSelectProdukt(product) : void 0;

      return html`<bh-workshop-card
        @click=${onClick}
        ${provideContext(workshopCardDataContext, workshopCardData)}
        .workshopCardData=${workshopCardData}
        ${css({ width: 'calc(100% - 20px)' })}
      >
        <div ${css({ width: '100%' })} ${acss({ ' > *': { width: '100%' } })}>${content}</div>
      </bh-workshop-card> `;
    }

    if (offer.type === 'counseling') {
      const content = (() => {
        if (typeof institution.productPageCounselingCard === 'string')
          return unsafeHTML(institution.productPageCounselingCard);

        return unsafeHTML(counselingCardBuildifyPreset);
      })();

      const counselingCardData = offer.counselingData;

      if (!counselingCardData.slotData) return html`<x-loader></x-loader>`;
      return html`<bh-counseling-card
        ${provideContext(counselingCardDataContext, counselingCardData)}
        ${css({ width: 'calc(100% - 20px)' })}
      >
        ${content}
      </bh-counseling-card> `;
    }
  }

  // to check i.e. against time conflicts
  private get allActiveProductsForUser() {
    return (
      this.registrationController.activeParticipations
        .filter(participation => [ParticipationState.IN, ParticipationState.WAITING].includes(participation.state))
        .filter(isDefined)
        .map(participation => participation.product) ?? []
    );
  }
}
